import { zodResolver } from '@hookform/resolvers/zod';
import {
  CreateHeroInclusionRequestSchema,
  type CreateHeroInclusionRequest,
  UpdateHeroInclusionRequestSchema,
  type UpdateHeroInclusionRequest,
  type HeroInclusionDetail,
  type HeroInclusionListItem,
} from '@utazzo/shared';
import {
  UzButton,
  UzDrawer,
  UzForm,
  UzFormField,
  UzInput,
  UzNumberInput,
  UzSelect,
} from '@utazzo/ui';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { useCreateHeroInclusion, useUpdateHeroInclusion } from '../use-hero-inclusions';

import { MediaDropField } from '@/features/website-cms/components/MediaDropField';

interface HeroInclusionFormDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  inclusion?: HeroInclusionDetail | HeroInclusionListItem | null;
}

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

const buildCreateDefaults = (): CreateHeroInclusionRequest => ({
  name: '',
  iconImageId: null,
  isActive: true,
  sortOrder: 0,
});

/** Shared field set. Reads the active form from context (UzForm = FormProvider). */
const InclusionFields = ({ pending }: { pending: boolean }): React.JSX.Element => {
  return (
    <>
      <UzFormField name="name">
        {({ field, error }) => (
          <UzInput
            {...field}
            value={(field.value as string | undefined) ?? ''}
            label="Name"
            placeholder="Flights"
            error={error}
            required
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="iconImageId">
        {({ field }) => (
          <MediaDropField
            value={field.value as string | null | undefined}
            onChange={field.onChange}
            label="Icon"
            kind="image"
            folder="hero-inclusions"
            variant="square"
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="isActive">
        {({ field, error }) => (
          <UzSelect
            name={field.name}
            label="Status"
            required
            options={STATUS_OPTIONS}
            value={field.value === false ? 'inactive' : 'active'}
            onValueChange={(v) => field.onChange(v === 'active')}
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="sortOrder">
        {({ field, error }) => (
          <UzNumberInput
            name={field.name}
            label="Sort order"
            precision={0}
            value={field.value === null || field.value === undefined ? '' : (field.value as number)}
            onValueChange={(v) => field.onChange(v ?? 0)}
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
    </>
  );
};

export const HeroInclusionFormDrawer = ({
  open,
  onOpenChange,
  mode,
  inclusion,
}: HeroInclusionFormDrawerProps): React.JSX.Element => {
  const isCreate = mode === 'create';
  const createInclusion = useCreateHeroInclusion();
  const updateInclusion = useUpdateHeroInclusion();
  const pending = createInclusion.isPending || updateInclusion.isPending;

  const createForm = useForm<CreateHeroInclusionRequest>({
    resolver: zodResolver(CreateHeroInclusionRequestSchema),
    defaultValues: buildCreateDefaults(),
  });
  const updateForm = useForm<UpdateHeroInclusionRequest>({
    resolver: zodResolver(UpdateHeroInclusionRequestSchema),
    defaultValues: {},
  });

  useEffect(() => {
    if (!open) return;
    if (isCreate) {
      createForm.reset(buildCreateDefaults());
    } else if (inclusion) {
      const d = inclusion as HeroInclusionDetail;
      updateForm.reset({
        name: d.name,
        iconImageId: d.iconImageId,
        isActive: d.isActive,
        sortOrder: d.sortOrder,
      });
    }
  }, [open, isCreate, inclusion, createForm, updateForm]);

  const onCreate = createForm.handleSubmit(async (values) => {
    await createInclusion.mutateAsync(values);
    onOpenChange(false);
  });

  const onUpdate = updateForm.handleSubmit(async (values) => {
    if (!inclusion) return;
    await updateInclusion.mutateAsync({ id: inclusion.id, payload: values });
    onOpenChange(false);
  });

  return (
    <UzDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={isCreate ? 'Add inclusion' : `Edit ${inclusion?.name ?? 'inclusion'}`}
      description="Hero-section inclusion: icon + name."
      footer={
        <div className="flex justify-end gap-2">
          <UzButton variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </UzButton>
          <UzButton onClick={isCreate ? onCreate : onUpdate} loading={pending}>
            {isCreate ? 'Submit' : 'Save changes'}
          </UzButton>
        </div>
      }
    >
      {isCreate ? (
        <UzForm form={createForm} onSubmit={onCreate}>
          <InclusionFields pending={pending} />
        </UzForm>
      ) : (
        <UzForm form={updateForm} onSubmit={onUpdate}>
          <InclusionFields pending={pending} />
        </UzForm>
      )}
    </UzDrawer>
  );
};
